import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  loadAssetRegistry,
  onAssetRegistryChanged,
} from "../core/assetRepository";
import { registryPalette } from "../core/assets";
import type { CreatorVerificationProof, ReactiveWorldsMapPackage } from "../types";
import {
  RUNTIME_URL,
  loadRuntime,
  postRuntime,
  requestId,
  type RuntimeMessage,
  type RuntimeMode,
} from "./runtimeClient";
import { useAuth } from "../context/AuthContext";
import {
  loadPlayerState,
  savePlayerState,
} from "../services/playerStateRepository";

export interface RuntimeFrameHandle {
  requestPackage: () => Promise<ReactiveWorldsMapPackage>;
  requestAvatar: () => Promise<string>;
  requestScreenshot: () => Promise<string>;
  restart: () => void;
  backToEditor: () => void;
  homeAction: (
    action: "furnish" | "edit" | "previous-room" | "next-room",
  ) => void;
  homeRoom: (roomId: string) => void;
  applyProfile: (profile: Record<string, unknown>) => void;
}
type DirectRuntimeApi = {
  exportMapPackage: () => ReactiveWorldsMapPackage;
  importMapPackage: (pkg: ReactiveWorldsMapPackage, mode?: RuntimeMode) => void;
  setAssetPayload: (payload: unknown) => void;
  applyProfile?: (profile: Record<string, unknown>) => void;
  setResultMeta?: (creatorName?: string, bountyGold?: number) => void;
  restart?: () => void;
  backToEditor?: () => void;
  screenshot: () => string;
};
const directRuntime = (frame: HTMLIFrameElement | null): DirectRuntimeApi | null => {
  try {
    return ((frame?.contentWindow as (Window & { ReactiveWorldsUnifiedRuntime?: DirectRuntimeApi }) | null)
      ?.ReactiveWorldsUnifiedRuntime) || null;
  } catch {
    return null;
  }
};
export type RuntimeSaveStatus = "loading" | "saving" | "synced" | "error";
export function RuntimeFrame({
  pkg,
  mode,
  mapId,
  onReady,
  onComplete,
  onShareCapture,
  onDeath,
  onPackageApi,
  onProfileChanged,
  onSaveStatus,
  onFullscreenChange,
  onRuntimeModeChange,
  profileOverride,
  readOnlyProfile = false,
  returnPath,
  levelTitle,
  creatorName,
  bountyGold = 0,
}: {
  pkg: ReactiveWorldsMapPackage;
  mode: RuntimeMode;
  mapId?: string;
  onReady?: () => void;
  onComplete?: (pkg: ReactiveWorldsMapPackage, proof: CreatorVerificationProof | null) => void;
  onShareCapture?: (dataUrl: string) => void;
  onDeath?: (reason?: string) => void;
  onPackageApi?: (api: RuntimeFrameHandle | null) => void;
  onProfileChanged?: (profile: Record<string, unknown>) => void;
  onSaveStatus?: (status: RuntimeSaveStatus) => void;
  onFullscreenChange?: (active: boolean) => void;
  onRuntimeModeChange?: (mode: string) => void;
  profileOverride?: Record<string, unknown> | null;
  readOnlyProfile?: boolean;
  returnPath?: string;
  levelTitle?: string;
  creatorName?: string;
  bountyGold?: number;
}) {
  const { user } = useAuth(),
    navigate = useNavigate(),
    ref = useRef<HTMLIFrameElement>(null),
    pending = useRef(
      new Map<
        string,
        {
          resolve: (p: ReactiveWorldsMapPackage) => void;
          reject: (e: Error) => void;
        }
      >(),
    ),
    avatarPending = useRef(
      new Map<
        string,
        {
          resolve: (dataUrl: string) => void;
          reject: (e: Error) => void;
        }
      >(),
    ),
    screenshotPending = useRef(
      new Map<
        string,
        {
          resolve: (dataUrl: string) => void;
          reject: (e: Error) => void;
        }
      >(),
    ),
    saveTimer = useRef<number | null>(null),
    saveRevision = useRef(0),
    latestProfile = useRef<Record<string, unknown> | null>(null),
    directEditorLoaded = useRef(false),
    [ready, setReady] = useState(false),
    [displayReady, setDisplayReady] = useState(false),
    [frameFullscreen, setFrameFullscreen] = useState(false),
    effectivePackage = useMemo(() => {
      const title = levelTitle?.trim();
      return title ? { ...pkg, ugc: { ...pkg.ugc, title } } : pkg;
    }, [levelTitle, pkg]),
    runtimeCreatorName = creatorName ||
      (typeof effectivePackage.ugc.creatorName === "string" ? effectivePackage.ugc.creatorName : "") ||
      (mode === "edit" ? user?.displayName : "") ||
      "Matter Maze Creator",
    runtimeBountyGold = bountyGold ||
      (typeof effectivePackage.ugc.bountyGold === "number" ? effectivePackage.ugc.bountyGold : 0);
  useEffect(() => {
    const onMessage = (e: MessageEvent<RuntimeMessage>) => {
      if (
        e.origin !== location.origin ||
        e.source !== ref.current?.contentWindow ||
        e.data?.source !== "reactive-worlds-runtime"
      )
        return;
      const m = e.data;
      if (m.type === "rw:ready") setReady(true);
      else if (m.type === "rw:loaded") setDisplayReady(true);
      else if (m.type === "rw:death") onDeath?.(m.reason);
      else if (m.type === "rw:profile-changed" && m.profile && user && !readOnlyProfile) {
        latestProfile.current = m.profile;
        onProfileChanged?.(m.profile);
        const revision = ++saveRevision.current;
        onSaveStatus?.("saving");
        if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
        saveTimer.current = window.setTimeout(() => {
          const state = latestProfile.current;
          if (state)
            void savePlayerState(user.uid, state)
              .then(() => {
                if (revision === saveRevision.current) onSaveStatus?.("synced");
              })
              .catch((error) => {
                if (revision === saveRevision.current) onSaveStatus?.("error");
                console.error("Player state save failed.", error);
              });
        }, 750);
      } else if (m.type === "rw:package" && m.requestId) {
        const p = pending.current.get(m.requestId);
        if (p && m.package) {
          pending.current.delete(m.requestId);
          p.resolve(m.package);
        }
      } else if (m.type === "rw:avatar" && m.requestId) {
        const p = avatarPending.current.get(m.requestId);
        if (p && m.dataUrl) {
          avatarPending.current.delete(m.requestId);
          p.resolve(m.dataUrl);
        }
      } else if (m.type === "rw:screenshot" && m.requestId) {
        const p = screenshotPending.current.get(m.requestId);
        if (p && m.dataUrl) {
          screenshotPending.current.delete(m.requestId);
          p.resolve(m.dataUrl);
        }
      } else if (m.type === "rw:share-completion") {
        onShareCapture?.(m.dataUrl || "");
      } else if (m.type === "rw:fullscreen") {
        const active = m.active === true;
        setFrameFullscreen(active);
        onFullscreenChange?.(active);
      } else if (m.type === "rw:mode" && m.mode) {
        onRuntimeModeChange?.(m.mode);
      } else if (m.type === "rw:runtime-error") {
        console.error("MatterMaze runtime error:", m.message || "Unknown runtime error");
        window.dispatchEvent(new CustomEvent("rw:runtime-error", { detail: { message: m.message || "Unknown runtime error", mode } }));
      } else if (m.type === "rw:error" && m.requestId) {
        const p = pending.current.get(m.requestId);
        if (p) {
          pending.current.delete(m.requestId);
          p.reject(new Error(m.message || "Runtime error"));
        }
        const avatar = avatarPending.current.get(m.requestId);
        if (avatar) {
          avatarPending.current.delete(m.requestId);
          avatar.reject(new Error(m.message || "Portrait capture failed"));
        }
        const screenshot = screenshotPending.current.get(m.requestId);
        if (screenshot) {
          screenshotPending.current.delete(m.requestId);
          screenshot.reject(new Error(m.message || "Screenshot capture failed"));
        }
      } else if (m.type === "rw:complete" && m.package) onComplete?.(m.package, m.proof || null);
      else if (m.type === "rw:navigate" && m.path) navigate(m.path);
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
      const state = latestProfile.current;
      if (state && user && !readOnlyProfile)
        void savePlayerState(user.uid, state).catch((error) =>
          console.error("Final player state save failed.", error),
        );
    };
  }, [mode, navigate, onComplete, onDeath, onFullscreenChange, onProfileChanged, onRuntimeModeChange, onSaveStatus, onShareCapture, readOnlyProfile, user]);
  useEffect(() => {
    if (!onFullscreenChange) return;
    type FullscreenDocument = Document & { webkitFullscreenElement?: Element | null };
    const active = () => {
      const page = document as FullscreenDocument;
      try {
        const child = ref.current?.contentDocument as FullscreenDocument | null;
        return !!(page.fullscreenElement || page.webkitFullscreenElement || child?.fullscreenElement || child?.webkitFullscreenElement || window.matchMedia?.("(display-mode: fullscreen)").matches);
      } catch {
        return !!(page.fullscreenElement || page.webkitFullscreenElement);
      }
    };
    const sync = () => {
      const next = active();
      setFrameFullscreen(next);
      onFullscreenChange(next);
    };
    const child = ref.current?.contentDocument as FullscreenDocument | null;
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync as EventListener);
    child?.addEventListener("fullscreenchange", sync);
    child?.addEventListener("webkitfullscreenchange", sync as EventListener);
    window.addEventListener("resize", sync);
    const timer = window.setInterval(sync, 500);
    sync();
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync as EventListener);
      child?.removeEventListener("fullscreenchange", sync);
      child?.removeEventListener("webkitfullscreenchange", sync as EventListener);
      window.removeEventListener("resize", sync);
      window.clearInterval(timer);
      onFullscreenChange(false);
    };
  }, [onFullscreenChange, ready]);
  useEffect(() => {
    if (!ready || !ref.current) return;
    let active = true;
    setDisplayReady(false);
    onSaveStatus?.("loading");
    const profileRequest = profileOverride
      ? Promise.resolve(profileOverride)
      : user
        ? loadPlayerState(user.uid).catch((error) => {
            console.warn("Player state unavailable; starting the runtime with its local profile.", error);
            return null;
          })
        : Promise.resolve(null);
    void Promise.all([loadAssetRegistry(), profileRequest])
      .then(([registry, profile]) => {
        if (!active || !registry || !ref.current) return;
        const assets = { registry, palette: registryPalette(registry) };
        const direct = directRuntime(ref.current);
        if (profile) {
          if (directEditorLoaded.current && direct?.applyProfile) direct.applyProfile(profile);
          else postRuntime(ref.current, "rw:profile", { profile, readOnlyProfile });
        }
        if (mode === "edit" && directEditorLoaded.current && direct) direct.setAssetPayload(assets);
        else
          loadRuntime(
            ref.current,
            effectivePackage,
            mode,
            assets,
            mapId,
            returnPath,
            readOnlyProfile,
            runtimeCreatorName,
            runtimeBountyGold,
          );
        if (mode === "edit" && directEditorLoaded.current) setDisplayReady(true);
        onSaveStatus?.("synced");
        onReady?.();
      })
      .catch((error) => {
        onSaveStatus?.("error");
        console.error("Runtime bootstrap failed.", error);
      });
    return () => {
      active = false;
    };
  }, [ready, effectivePackage, mode, mapId, onReady, onSaveStatus, profileOverride, readOnlyProfile, returnPath, user, runtimeCreatorName, runtimeBountyGold]);
  useEffect(
    () =>
      onAssetRegistryChanged(() => {
        if (!ready || !ref.current) return;
        void loadAssetRegistry().then((registry) => {
          if (!registry || !ref.current) return;
          const assets = { registry, palette: registryPalette(registry) };
          const direct = directRuntime(ref.current);
          if (directEditorLoaded.current && direct) direct.setAssetPayload(assets);
          else postRuntime(ref.current, "rw:assets", { assets });
        });
      }),
    [ready],
  );
  useEffect(() => {
    const api: RuntimeFrameHandle = {
      requestPackage: () => {
        const direct = directRuntime(ref.current);
        if (direct) {
          try {
            return Promise.resolve(direct.exportMapPackage());
          } catch (error) {
            return Promise.reject(error instanceof Error ? error : new Error("Runtime export failed"));
          }
        }
        return new Promise((resolve, reject) => {
          if (!ref.current) return reject(new Error("Runtime unavailable"));
          const id = requestId("pkg");
          pending.current.set(id, { resolve, reject });
          postRuntime(ref.current, "rw:request-package", { requestId: id });
          window.setTimeout(() => {
            const p = pending.current.get(id);
            if (p) {
              pending.current.delete(id);
              reject(new Error("Runtime package request timed out"));
            }
          }, 5000);
        });
      },
      requestAvatar: () =>
        new Promise((resolve, reject) => {
          if (!ref.current) return reject(new Error("Runtime unavailable"));
          const id = requestId("avatar");
          avatarPending.current.set(id, { resolve, reject });
          postRuntime(ref.current, "rw:request-avatar", { requestId: id });
          window.setTimeout(() => {
            const p = avatarPending.current.get(id);
            if (p) {
              avatarPending.current.delete(id);
              reject(new Error("Character portrait request timed out"));
            }
          }, 8000);
        }),
      requestScreenshot: () => {
        const direct = directRuntime(ref.current);
        if (direct) {
          try {
            return Promise.resolve(direct.screenshot());
          } catch (error) {
            return Promise.reject(error instanceof Error ? error : new Error("Screenshot failed"));
          }
        }
        return new Promise((resolve, reject) => {
          if (!ref.current) return reject(new Error("Runtime unavailable"));
          const id = requestId("screenshot");
          screenshotPending.current.set(id, { resolve, reject });
          postRuntime(ref.current, "rw:screenshot", { requestId: id });
          window.setTimeout(() => {
            const p = screenshotPending.current.get(id);
            if (p) {
              screenshotPending.current.delete(id);
              reject(new Error("Screenshot request timed out"));
            }
          }, 8000);
        });
      },
      restart: () => {
        const direct = directRuntime(ref.current);
        if (direct?.restart) direct.restart();
        else if (ref.current) postRuntime(ref.current, "rw:restart");
      },
      backToEditor: () => {
        const direct = directRuntime(ref.current);
        if (direct?.backToEditor) direct.backToEditor();
        else if (ref.current) postRuntime(ref.current, "rw:back-to-editor");
      },
      homeAction: (action) => {
        if (ref.current && (!readOnlyProfile || action === "previous-room" || action === "next-room"))
          postRuntime(ref.current, "rw:home-action", { action });
      },
      homeRoom: (roomId) => {
        if (ref.current && roomId) postRuntime(ref.current, "rw:home-action", { action: "room", roomId });
      },
      applyProfile: (profile) => {
        const direct = directRuntime(ref.current);
        if (direct?.applyProfile) direct.applyProfile(profile);
        else if (ref.current)
          postRuntime(ref.current, "rw:profile", { profile, readOnlyProfile });
      },
    };
    onPackageApi?.(api);
    return () => onPackageApi?.(null);
  }, [onPackageApi, readOnlyProfile]);
  const frame = (
    <div className={`runtime-frame-shell${displayReady ? " ready" : " loading"}${frameFullscreen ? " runtime-fullscreen" : ""}`}>
    <iframe
      ref={ref}
      className="runtime-frame"
      src={RUNTIME_URL}
      onLoad={() => {
        setReady(false);
        setDisplayReady(false);
        setFrameFullscreen(false);
        onFullscreenChange?.(false);
        directEditorLoaded.current = false;
        if (ref.current) postRuntime(ref.current, "rw:hello");
        if (mode === "edit") {
          const direct = directRuntime(ref.current);
          if (direct) {
            direct.setResultMeta?.(runtimeCreatorName, runtimeBountyGold);
            direct.importMapPackage(effectivePackage, "edit");
            directEditorLoaded.current = true;
            setReady(true);
            onReady?.();
          }
        }
      }}
      title={
        mode === "edit" ? "MatterMaze Editor" : "MatterMaze Game"
      }
    />
      {!displayReady && <div className="runtime-pixel-loader" role="status" aria-live="polite">
        <div className="runtime-pixel-spinner" aria-hidden="true"><i/><i/><i/><i/></div>
        <b>LOADING MAZE</b>
      </div>}
    </div>
  );
  return frame;
}
